import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { supabase } from '../../../lib/supabase';
import { parseAndMap } from '../../../lib/excelMappersBlack';
import Sidebar from '../../admin-dashboard/components/Sidebar';

/**
 * Admin V2 Data Injections — Module d'upload Excel Generic (R9).
 *
 * Route : /admin/data-injections + /admin/v2/data-injections.
 * Feature : pages.config.data_injections.
 *
 * État Sprint 6 : SHELL UI. Les 3 schémas Excel (type1/type2/type3) ne sont
 * pas encore définis (décision produit en attente — cf. CLAUDE.md R9). Cette
 * page fournit :
 *  - 3 zones drag-drop (type1/type2/type3) — UI prête, parser à brancher
 *  - l'historique des imports (table data_imports)
 *
 * Le parser + dry-run + idempotency par période seront branchés une fois les
 * schémas validés avec l'utilisateur (mirroir de `data-import-tcn`).
 */
const FILE_TYPES = [
  { id: 'type1', accent: 'border-primary/40' },
  { id: 'type2', accent: 'border-success/40' },
  { id: 'type3', accent: 'border-accent/40' },
];

const DropZone = ({ typeId, accent, server, agencyId, onApplied }) => {
  const { t } = useTranslation();
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [periodLabel, setPeriodLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const reset = () => { setFile(null); setDryRun(null); setResult(null); setErr(null); setPeriodLabel(''); };

  const handleFile = async (f) => {
    if (!f) return;
    setFile(f); setErr(null); setResult(null); setDryRun(null);
    if (!server) { setErr(t('injectV2.pickServer', 'Pick a server first.')); return; }
    if (!agencyId) { setErr(t('injectV2.noAgency', 'Your account has no agency.')); return; }
    setBusy(true);
    try {
      const rows = await parseAndMap(f, typeId, server, periodLabel || null);
      if (rows.length === 0) { setErr(t('injectV2.noRows', 'No valid rows found in this file.')); setBusy(false); return; }
      const { data, error } = await supabase.functions.invoke('data-import-black/dry-run', {
        body: { fileType: typeId, server, agencyId, periodLabel: periodLabel || null, rows, fileName: f?.name || null },
      });
      if (error) throw error;
      setDryRun({ ...data, _rows: rows });
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!dryRun?._rows) return;
    setBusy(true); setErr(null);
    try {
      const { data, error } = await supabase.functions.invoke('data-import-black/apply', {
        body: { fileType: typeId, server, agencyId, periodLabel: periodLabel || null, rows: dryRun._rows, fileName: file?.name || null },
      });
      if (error) throw error;
      setResult(data);
      onApplied?.();
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-card border border-border overflow-hidden">
      <div className="px-5 py-3 border-b border-border bg-surface-2">
        <div className="font-heading font-semibold text-foreground text-sm">
          {t(`injectV2.${typeId}.title`, typeId.toUpperCase())}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {t(`injectV2.${typeId}.desc`, '')}
        </div>
      </div>
      <div className="p-5 space-y-3">
        {typeId === 'type2' && (
          <input
            type="text"
            value={periodLabel}
            onChange={(e) => setPeriodLabel(e.target.value)}
            placeholder={t('injectV2.periodPlaceholder', 'Period YYYY-MM (optional override)')}
            className="w-full px-3 py-2 rounded-lg bg-background border border-border text-base focus:outline-none focus:ring-1 focus:ring-primary"
          />
        )}
        {!dryRun && !result && (
          <label
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer?.files?.[0]); }}
            className={`flex flex-col items-center justify-center gap-2 py-8 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
              dragOver ? 'border-primary bg-primary/5' : `${accent} bg-background/40 hover:bg-muted/30`
            } ${busy ? 'opacity-60 pointer-events-none' : ''}`}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" className="text-muted-foreground">
              <path d="M12 16V4m0 0L8 8m4-4l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <div className="text-sm text-foreground font-medium">
              {busy ? t('injectV2.parsing', 'Parsing…') : t('injectV2.dropHint', 'Drop your Excel file here')}
            </div>
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" disabled={busy}
              onChange={(e) => handleFile(e.target.files?.[0])} />
            <div className="text-[10px] text-muted-foreground/70">.xlsx · .xls · .csv</div>
          </label>
        )}

        {err && <div className="rounded-lg bg-error/5 border border-error/20 px-3 py-2 text-xs text-error">{err}</div>}

        {dryRun && !result && (
          <div className="rounded-lg bg-primary/5 border border-primary/20 px-3 py-3 text-xs space-y-2">
            <div className="font-semibold text-primary">{t('injectV2.dryRunTitle', 'Dry-run preview')}</div>
            <div className="grid grid-cols-2 gap-1 text-muted-foreground">
              <span>{t('injectV2.totalRows', 'Rows')}: <b className="text-foreground">{dryRun.totalRows}</b></span>
              <span>{t('injectV2.uniqueCreators', 'Creators')}: <b className="text-foreground">{dryRun.uniqueCreators}</b></span>
              <span>{t('injectV2.existing', 'Existing')}: <b className="text-foreground">{dryRun.existingCreators}</b></span>
              {dryRun.newCreators != null && <span>{t('injectV2.new', 'New')}: <b className="text-foreground">{dryRun.newCreators}</b></span>}
            </div>
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={apply} disabled={busy}
                className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium disabled:opacity-60">
                {busy ? t('injectV2.applying', 'Applying…') : t('injectV2.apply', 'Apply import')}
              </button>
              <button type="button" onClick={reset} disabled={busy}
                className="px-3 py-1.5 rounded-lg border border-border text-xs">
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        )}

        {result && (
          <div className="rounded-lg bg-success/5 border border-success/20 px-3 py-3 text-xs space-y-2">
            <div className="font-semibold text-success">{t('injectV2.done', 'Import applied')}</div>
            <div className="text-muted-foreground">
              {t('injectV2.upserted', 'Upserted')}: <b className="text-foreground">{result.upserted}</b>
              {result.skippedNoCreator > 0 && <> · {t('injectV2.skipped', 'Skipped (no creator)')}: <b className="text-foreground">{result.skippedNoCreator}</b></>}
            </div>
            <button type="button" onClick={reset} className="px-3 py-1.5 rounded-lg border border-border text-xs">
              {t('injectV2.importAnother', 'Import another')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminV2DataInjections = () => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { tenantName, tenantId, agencyId, servers } = useTenant();
  const [collapsed, setCollapsed] = useState(false);
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const serverOptions = (servers && servers.length > 0) ? servers : ['MENA', 'FR+', 'IT+', 'DE+', 'US+'];
  const [selectedServer, setSelectedServer] = useState(serverOptions[0]);

  const loadImports = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const { data } = await supabase
        .from('black_data_imports')
        .select('id, file_name, period_label, status, created_at')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false })
        .limit(20);
      setImports(data || []);
    } catch {
      setImports([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadImports(); }, [loadImports]);

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} userEmail={userProfile?.email} />
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4">
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">
              {t('injectV2.title', 'Data injections')}
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">{tenantName}</p>
          </div>
        </header>

        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          <div className="rounded-2xl bg-card border border-border p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground mr-1">{t('injectV2.serverLabel', 'Target server:')}</span>
              {t('injectV2.serverHint', 'all uploaded rows are tagged with this server.')}
            </div>
            <select
              value={selectedServer}
              onChange={(e) => setSelectedServer(e.target.value)}
              className="px-3 py-2 rounded-lg bg-background border border-border text-base focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {serverOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {FILE_TYPES.map((ft) => (
              <DropZone
                key={ft.id}
                typeId={ft.id}
                accent={ft.accent}
                server={selectedServer}
                agencyId={agencyId}
                onApplied={loadImports}
              />
            ))}
          </div>

          {/* Import history */}
          <section className="rounded-2xl bg-card border border-border overflow-hidden">
            <div className="px-5 py-3 border-b border-border bg-surface-2 font-heading font-semibold text-foreground text-sm">
              {t('injectV2.history', 'Import history')}
            </div>
            {loading ? (
              <div className="h-24 skeleton" />
            ) : imports.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-muted-foreground">
                {t('injectV2.noImports', 'No imports recorded yet.')}
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {imports.map((imp) => (
                  <li key={imp.id} className="px-5 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-foreground truncate">{imp.file_name || '—'}</div>
                      <div className="text-xs text-muted-foreground">
                        {imp.period_label || '—'} · {imp.created_at ? new Date(imp.created_at).toLocaleString() : ''}
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-muted text-muted-foreground shrink-0">
                      {imp.status || '—'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default AdminV2DataInjections;
